/**
 * Number formatting utilities for Persian/RTL display
 */

// Persian to English digit mapping
const persianToEnglish = {
  '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4',
  '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9'
};

// English to Persian digit mapping
const englishToPersian = {
  '0': '۰', '1': '۱', '2': '۲', '3': '۳', '4': '۴',
  '5': '۵', '6': '۶', '7': '۷', '8': '۸', '9': '۹'
};

/**
 * Convert Persian digits to English digits
 * @param {string} str - String containing Persian digits
 * @returns {string} String with English digits
 */
export function persianToEnglishDigits(str) {
  if (!str) return str;
  return str.toString().replace(/[۰-۹]/g, (match) => persianToEnglish[match]);
}

/**
 * Convert English digits to Persian digits
 * @param {string} str - String containing English digits
 * @returns {string} String with Persian digits
 */
export function englishToPersianDigits(str) {
  if (!str) return str;
  return str.toString().replace(/[0-9]/g, (match) => englishToPersian[match]);
}

/**
 * Format number with thousands separators (comma)
 * @param {number|string} number - Number to format
 * @param {boolean} usePersianDigits - Whether to use Persian digits
 * @returns {string} Formatted number string
 */
export function formatNumber(number, usePersianDigits = false) {
  if (number === null || number === undefined || number === '') return '0';
  
  // Convert to number if string
  const num = typeof number === 'string' ? parseFloat(persianToEnglishDigits(number)) : number;
  
  if (isNaN(num)) return '0';
  
  // Format with thousands separators
  const formatted = num.toLocaleString('en-US');
  
  // Convert to Persian digits if requested
  return usePersianDigits ? englishToPersianDigits(formatted) : formatted;
}

/**
 * Format price with currency unit
 * @param {number|string} price - Price to format
 * @param {string} currency - Currency unit (default: 'تومان')
 * @param {boolean} usePersianDigits - Whether to use Persian digits
 * @returns {string} Formatted price string
 */
export function formatPrice(price, currency = 'تومان', usePersianDigits = false) {
  const formattedNumber = formatNumber(price, usePersianDigits);
  return `${formattedNumber} ${currency}`;
}

/**
 * Parse formatted number string to actual number
 * @param {string} formattedNumber - Formatted number string
 * @returns {number} Parsed number
 */
export function parseFormattedNumber(formattedNumber) {
  if (!formattedNumber) return 0;
  
  // Convert Persian digits to English and remove commas
  const cleanNumber = persianToEnglishDigits(formattedNumber.toString()).replace(/,/g, '');
  const parsed = parseFloat(cleanNumber);
  
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Validate if string contains only numeric characters (including Persian digits)
 * @param {string} str - String to validate
 * @returns {boolean} True if valid numeric string
 */
export function isValidNumericString(str) {
  if (!str) return false;
  const englishStr = persianToEnglishDigits(str.toString());
  return /^[0-9.,]+$/.test(englishStr);
}

/**
 * Clean and format input for numeric fields
 * @param {string} input - Raw input string
 * @param {number} maxLength - Maximum length allowed
 * @returns {string} Cleaned and formatted input
 */
export function cleanNumericInput(input, maxLength = null) {
  if (!input) return '';
  
  // Convert Persian digits to English
  let cleaned = persianToEnglishDigits(input.toString());
  
  // Remove non-numeric characters except dots and commas
  cleaned = cleaned.replace(/[^0-9.,]/g, '');
  
  // Remove all commas (they are thousands separators, not decimal separators)
  cleaned = cleaned.replace(/,/g, '');
  
  // Handle multiple dots - keep only the first one as decimal separator
  const dotIndex = cleaned.indexOf('.');
  if (dotIndex !== -1) {
    const intPart = cleaned.substring(0, dotIndex);
    const fracPart = cleaned.substring(dotIndex + 1).replace(/\./g, '');
    cleaned = intPart + '.' + fracPart;
  }
  
  // Apply length limit
  if (maxLength && cleaned.length > maxLength) {
    cleaned = cleaned.substring(0, maxLength);
  }
  
  return cleaned;
}

/**
 * Enforce decimal limit on a numeric string
 * @param {string} value - Numeric string value
 * @param {number} decimalsLimit - Maximum decimal places allowed
 * @returns {string} Value with enforced decimal limit
 */
export function enforceDecimalLimit(value, decimalsLimit) {
  if (decimalsLimit === null || decimalsLimit === undefined) {
    return value;
  }
  
  const parts = value.split('.');
  if (parts.length === 1) {
    return value;
  }
  
  const intPart = parts[0];
  let fracPart = parts[1] || '';
  
  if (decimalsLimit <= 0) {
    return intPart;
  }
  
  fracPart = fracPart.slice(0, decimalsLimit);
  return fracPart ? `${intPart}.${fracPart}` : intPart;
}

/**
 * Format number input in real-time as user types
 * @param {string} input - Current input value
 * @param {number} cursorPosition - Current cursor position
 * @returns {object} Object with formatted value and new cursor position
 */
export function formatInputRealTime(input, cursorPosition) {
  if (!input) return { value: '', cursorPosition: 0 };
  
  // Clean the input
  const cleaned = cleanNumericInput(input);
  if (!cleaned) return { value: '', cursorPosition: 0 };
  
  // Split into integer and decimal parts
  const parts = cleaned.split('.');
  const intPart = parts[0] || '';
  const decimalPart = parts[1];
  
  // Count commas before cursor position to adjust cursor
  const beforeCursor = input.substring(0, cursorPosition);
  const commasBefore = (beforeCursor.match(/,/g) || []).length;
  
  // Format the integer part with commas
  let formattedInt = '';
  if (intPart) {
    const num = parseInt(intPart, 10);
    if (!isNaN(num)) {
      formattedInt = num.toLocaleString('en-US');
    }
  }
  
  // Combine integer and decimal parts
  const formatted = decimalPart !== undefined ? `${formattedInt}.${decimalPart}` : formattedInt;
  
  // Calculate new cursor position accounting for added/removed commas
  const newCommasBefore = (formatted.substring(0, cursorPosition).match(/,/g) || []).length;
  const cursorAdjustment = newCommasBefore - commasBefore;
  const newCursorPosition = Math.max(0, cursorPosition + cursorAdjustment);
  
  return {
    value: formatted,
    cursorPosition: Math.min(newCursorPosition, formatted.length)
  };
}